import React from "react";
import { Link, useNavigate } from "react-router-dom";

import countries from "../config/countries";
import nigStates from "../config/nigStates";
import FormInput from "src/components/widgets/FormInput";
import FormSelect from "src/components/widgets/FormSelect";

import { Button } from "src/components/ui/button";
import { Formik } from "formik";
import * as Yup from "yup";
import { useMutation, useQuery } from "@tanstack/react-query";
import { register } from "src/api/auth";
import { showSuccessToast } from "src/lib/utils";
import PasswordInput from "src/components/widgets/PasswordInput";
import { getBusinessOrganizationTypes } from "src/api";

const initialValues = {
	first_name: "",
	last_name: "",
	email: "",
	phone: "",
	business_name: "",
	business_address: "",
	organization_type_id: "",
	country: "",
	state: "",
	password: "",
	password_confirmation: "",
};
const validationSchema = Yup.object().shape({
	first_name: Yup.string().required("First name is required"),
	last_name: Yup.string().required("Last name is required"),
	email: Yup.string().email("Invalid email address").required("Email is required"),
	phone: Yup.string().required("Phone is required"),
	business_name: Yup.string().required("Business name is required"),
	business_address: Yup.string().required("Business address is required"),
	organization_type_id: Yup.string().required("Business Type is required"),
	country: Yup.string().required("Country is required"),
	state: Yup.string().required("State is required"),
	password: Yup.string().min(8, "Password must be at least 8 characters").required("Password is required"),
	password_confirmation: Yup.string()
		.oneOf([Yup.ref("password"), null], "Passwords must match")
		.required("Confirm password is required"),
});

function Signup() {
	const navigate = useNavigate();

	const { data: rawOrganizationType } = useQuery({
		queryKey: ["business-organization-types"],
		queryFn: getBusinessOrganizationTypes,
		suspense: true,
	});

	const organizationTypes = rawOrganizationType?.data ?? [];

	const { mutate, isLoading } = useMutation(register, {
		onSuccess: ({ message }, { email }) => {
			showSuccessToast(message);

			navigate("/otp-verification", { state: { email } });
		},
	});
	return (
		<div className="tw-max-w-[600px] tw-w-full tw-mx-auto">
			<div className="pageTitle">
				<h1>Create an Account</h1>
				<p>Complete the sign-up process to get started</p>
			</div>

			<Formik
				validateOnBlur={false}
				validateOnMount={false}
				initialValues={initialValues}
				validationSchema={validationSchema}
				onSubmit={(values) => mutate(values)}
			>
				{({ values, handleChange, handleSubmit, errors, setFieldValue }) => (
					<div className="signUpForm">
						<form onSubmit={handleSubmit}>
							<FormInput
								className="formItem !tw-w-full"
								label="First Name"
								name="first_name"
								type="text"
								placeholder="First Name"
								value={values.first_name}
								onChange={handleChange}
								error={errors.first_name}
							/>

							<FormInput
								className="formItem !tw-w-full"
								label="Last Name"
								name="last_name"
								type="text"
								placeholder="Last Name"
								value={values.last_name}
								onChange={handleChange}
								error={errors.last_name}
							/>

							<FormInput
								className="formItem !tw-w-full"
								label="Email"
								name="email"
								type="email"
								placeholder="Email"
								value={values.email}
								onChange={handleChange}
								error={errors.email}
								autoComplete="off"
							/>

							<FormInput
								className="formItem !tw-w-full"
								label="Phone"
								name="phone"
								type="text"
								placeholder="Phone"
								value={values.phone}
								onChange={handleChange}
							/>

							<FormInput
								className="formItem !tw-w-full"
								label="Business Name"
								name="business_name"
								type="text"
								placeholder="Business Name"
								value={values.business_name}
								onChange={handleChange}
								error={errors.business_name}
							/>

							<FormInput
								className="formItem !tw-w-full"
								label="Business Address"
								name="business_address"
								type="text"
								placeholder="Business Address"
								value={values.business_address}
								onChange={handleChange}
								error={errors.business_address}
							/>

							<FormSelect
								className="formItem !tw-w-full"
								label="Business Type"
								name="organization_type_id"
								value={values.organization_type_id}
								options={organizationTypes.map((cat) => ({ value: String(cat.id), label: cat.name }))}
								onChange={(e) => setFieldValue("organization_type_id", e.value)}
								error={errors.organization_type_id}
							/>

							<FormSelect
								className="formItem !tw-w-full"
								label="Country"
								name="country"
								value={values.country}
								options={countries.map((country) => ({ value: country.name, label: country.name }))}
								onChange={(e) => setFieldValue("country", e.value)}
								error={errors.country}
							/>

							<FormSelect
								className="formItem !tw-w-full"
								label="State"
								name="state"
								value={values.state}
								options={nigStates.map((state) => ({ value: state.name, label: state.name }))}
								onChange={(e) => setFieldValue("state", e.value)}
								error={errors.state}
							/>

							<PasswordInput
								className="formItem !tw-w-full"
								label="Password"
								name="password"
								type="password"
								placeholder="Password"
								value={values.password}
								onChange={handleChange}
								error={errors.password}
								autoComplete="off"
							/>

							<PasswordInput
								label="Confirm Password"
								name="password_confirmation"
								type="password"
								placeholder="Confirm Password"
								value={values.password_confirmation}
								onChange={handleChange}
								error={errors.password_confirmation}
								autoComplete="off"
								className="formItem !tw-w-full"
							/>

							<Button variant="secondary" type="submit" className="tw-w-full" disabled={isLoading}>
								{isLoading ? "Creating..." : "Create Account"}
							</Button>

							<p className="authFormText">
								Already Signed up. <Link to="/login">Click here to login</Link>
							</p>

							<p className="authFormText">
								By clicking <span className="color_teal">'Create Account'</span>, I agree to Paylinq Solutions' <br />
								Terms of Service and Privacy Policy
							</p>
						</form>
					</div>
				)}
			</Formik>
		</div>
	);
}

export default Signup;
